import { TAB_EVENT } from './Tabs'
import type { Readiness } from './api'
import { translate, type Lang } from './i18n'
import { fmtDate, fmtTime } from './time'

/** A qué área se va para hacer cada paso. */
const AREA_OF: Record<string, string> = {
  officers: 'hr', vehicles: 'fleet', hotspots: 'analyst', strategy: 'strategy',
  units: 'planner', proposal: 'planner', approval: 'planner',
}

/**
 * En qué pestaña del área está cada paso. «Ir» abría la última pestaña usada: el jefe de turno
 * que iba a aprobar caía en Escenarios y tenía que buscar el plan (H-96).
 */
const TAB_OF: Record<string, [string, string]> = {
  hotspots: ['analyst', 'hotspots'], units: ['planner', 'unidades'],
  proposal: ['planner', 'escenarios'], approval: ['planner', 'plan'],
}

function abrirPestaña(paso: string) {
  const destino = TAB_OF[paso]
  if (!destino) return
  try { localStorage.setItem(`tab:${destino[0]}`, destino[1]) } catch { /* sin almacenamiento: abre la primera */ }
  // Si ya se está en esa área, sus pestañas no releen lo guardado: se les avisa. Sin esto «Ir» del
  // analista se quedaba en Datos en vez de abrir Hotspots (demo, 27-sep).
  window.dispatchEvent(new CustomEvent(TAB_EVENT, { detail: { area: destino[0], tab: destino[1] } }))
}

/**
 * Qué le toca hoy (hallazgo H-68, decidido el 21 de septiembre).
 *
 * El ciclo del turno que se prepara, en el orden de §6: qué está hecho, qué falta y a quién le toca.
 * Quien entra ya no tiene que saber de memoria que el planificador espera a RH, ni descubrir por un
 * escenario vacío que el analista no publicó los hotspots de la ventana. Si el siguiente paso es
 * suyo, se lo dice con un botón para ir; si es de otro, le dice de quién.
 */
export function ReadinessPanel({
  lang, readiness, can, onGo,
}: {
  lang: Lang
  readiness: Readiness | null
  can: ReadonlySet<string>
  /** Llevar al área donde se hace el paso. */
  onGo: (area: string) => void
}) {
  if (!readiness) return null
  const siguiente = readiness.steps.find((s) => !s.done)
  const mio = siguiente && can.has(siguiente.capability)

  return (
    <section className="panel readiness" aria-label={translate(lang, 'ready.title', [readiness.shiftCode, ''])}>
      <h2>
        {translate(lang, 'ready.title', [
          readiness.shiftCode, `${fmtDate(readiness.start, lang, { day: 'numeric', month: 'short' })} ${fmtTime(readiness.start, lang)}`,
        ])}
      </h2>
      <ol className="readiness-steps">
        {readiness.steps.map((s) => (
          <li key={s.id} className={s.done ? 'done' : s === siguiente ? 'next' : 'pending'}>
            <span aria-hidden="true">{s.done ? '✓' : s === siguiente ? '→' : '·'}</span>{' '}
            {translate(lang, `ready.step.${s.id}`)}
            {s.count != null && s.done && <span className="meta"> · {s.count}</span>}
            {s.id === 'proposal' && s.detail.startsWith('returned:') && (
              <span className="meta"> · {translate(lang, 'ready.returned', [s.detail.slice('returned:'.length)])}</span>
            )}
          </li>
        ))}
      </ol>
      {!siguiente && <p className="notice">{translate(lang, 'ready.allDone')}</p>}
      {siguiente && mio && (
        <p className="notice">
          <strong>{translate(lang, 'ready.yourTurn', [translate(lang, `ready.step.${siguiente.id}`)])}</strong>{' '}
          <button onClick={() => { abrirPestaña(siguiente.id); onGo(AREA_OF[siguiente.id] ?? 'planner') }}>{translate(lang, 'ready.go')}</button>
        </p>
      )}
      {siguiente && !mio && (
        <p className="notice">
          {translate(lang, 'ready.waiting', [translate(lang, `ready.who.${siguiente.id}`)])}
        </p>
      )}
    </section>
  )
}
